import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../Utility/Input/Input";
import Label from "../../Utility/Label/Label";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import ToggleSwitch from "../../Utility/ToggleSwitch/ToggleSwitch";
import FileDropzone from "../../Utility/FileDropzone/FileDropzone";
import { logger } from "../../utils/logger";
import { toDateOnlyApiParam } from "../../utils/dateUtils";
import "./clients.css";
import ErrorMsg from "../../Utility/ErrorMsg";
import { AddClientSchema } from "../../FormsValidations/Validations";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import {
  createClient,
  fetchClientsData,
  uploadClientAttachments,
  fetchSaasModules,
  setClientSaasModules,
} from "../../Redux/Thunks/clientsThunk";
import { getListFromApiResponse } from "../utils/apiListMappers";
import {
  formToClientCreatePayload,
  getErrorMessage,
  buildClientsListFetchParams,
  extractCreatedClientId,
  normalizeClientAttachmentFiles,
  ITP_ACCESS_DROPDOWN_OPTIONS,
} from "./clientsDataUtils";
import { validateUploadFiles } from "../../utils/fileUploadValidation";
import ClientSaasModulesSection from "./ClientSaasModulesSection";
import { showSuccess, showError } from "../../utils/toast";
import { isGlobalApiErrorModalOpen } from "../../utils/globalApiErrorModal";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { selectSystemListItems } from "../../Redux/Slices/systemListsSlice";
import { mapSystemListItemToOption } from "../Issues/issueMappers";

export default function AddClient() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPath = location.state?.previousPath || "/clients";

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    unregister,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddClientSchema),
    defaultValues: {
      clientName: "",
      qbClientName: "",
      contactPerson: "",
      status: "Active",
      contactEmail: "",
      maxHoursMonthly: "",
      hoursLimitPeriod: "Monthly",
      hoursLimit: "",
      notes: "",
      isSaasClient: false,
      saasPlanName: "",
      saasBillingCycle: "",
      saasModules: {},
      serverHosted: "",
      ipAddress: "",
      dbIpAddress: "",
      dbLanguageId: "",
      mailHosted: "",
      domainName: "",
      dns: "",
      domainRegistrar: "",
      itpAccess: "No",
    },
  });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [saasModuleList, setSaasModuleList] = useState([]);
  const [assignedSaasModuleIds, setAssignedSaasModuleIds] = useState([]);

  const isSaasClient = watch("isSaasClient");

  const dbLanguageList = useSelector((s) => selectSystemListItems(s, "DB_LANGUAGE")) ?? [];
  const dbLanguageOptions = useMemo(
    () => dbLanguageList.map((item) => mapSystemListItemToOption(item, {})),
    [dbLanguageList]
  );

  useEffect(() => {
    dispatch(fetchSaasModules())
      .unwrap()
      .then((raw) => setSaasModuleList(getListFromApiResponse(raw)))
      .catch(() => setSaasModuleList([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchSystemList({
        listType: "DB_LANGUAGE",
        context: "add-client-db-language",
        force: true,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!isSaasClient && assignedSaasModuleIds.length > 0) {
      assignedSaasModuleIds.forEach((mid) => unregister(`saasModules.${mid}`));
      setAssignedSaasModuleIds([]);
    }
  }, [isSaasClient, assignedSaasModuleIds, unregister]);

  const onSubmit = async (data) => {
    setLoading(true);
    setSubmitError("");
    try {
      const attachmentFiles = normalizeClientAttachmentFiles(attachments);
      if (attachmentFiles.length > 10) {
        showError("Maximum 10 files allowed.");
        setSubmitError("Maximum 10 files allowed.");
        setLoading(false);
        return;
      }
      if (attachmentFiles.length > 0) {
        const attachmentValidationError = await validateUploadFiles(attachmentFiles, {
          inspectContent: false,
        });
        if (attachmentValidationError) {
          showError(attachmentValidationError);
          setSubmitError(attachmentValidationError);
          setLoading(false);
          return;
        }
      }

      const payload = formToClientCreatePayload(data);
      const result = await dispatch(createClient(payload)).unwrap();
      const clientId = extractCreatedClientId(result);

      if (attachmentFiles.length > 0) {
        if (!clientId) {
          const msg = "Client created, but attachment upload could not run (missing client id).";
          showError(msg);
          setSubmitError(msg);
          setLoading(false);
          return;
        }
        await dispatch(uploadClientAttachments({ clientId, files: attachmentFiles })).unwrap();
      }

      if (payload.isSaasClient && clientId && saasModuleList.length > 0) {
        const modules = assignedSaasModuleIds.map((modId) => {
          const row = data.saasModules?.[modId] ?? {};
          return {
            moduleId: modId,
            implementationFee: Number(row.implementationFee) || 0,
            monthlyFee: Number(row.monthlyFee) || 0,
            additionalFee: String(row.additionalFee ?? "").trim() || null,
            billingType: row.billingType || "both",
            startDate: toDateOnlyApiParam(row.startDate),
            endDate: toDateOnlyApiParam(row.endDate),
            isActive: !!row.active,
          };
        });
        try {
          await dispatch(setClientSaasModules({ clientId, modules })).unwrap();
        } catch (saasErr) {
          logger.error("Error setting SaaS modules:", saasErr);
          const msg = getErrorMessage(saasErr) || "Client created but SaaS modules failed to save.";
          if (!isGlobalApiErrorModalOpen()) {
            showError(msg);
            setSubmitError(msg);
          }
          setLoading(false);
          return;
        }
      }

      showSuccess("Client added successfully");
      const listParams =
        location.state?.clientsFetchParams ??
        buildClientsListFetchParams({ page: 1, listingFilter: {} });
      dispatch(fetchClientsData(listParams));
      navigate(prevPath);
    } catch (error) {
      logger.error("Error adding client:", error);
      if (isGlobalApiErrorModalOpen()) return;
      const message = getErrorMessage(error) || "Failed to create client";
      showError(message);
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Start right Content here */}
      <div className="w-full">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-2 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <Typography variant="h4" className="mb-0 text-theme-primary">
                    Add Client
                  </Typography>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm mt-2 mb-5">
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {submitError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                      {submitError}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4">
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="clientName" required className="mb-1">
                        Client Name
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("clientName")}
                          className={`w-full  border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.clientName ? "border-badge-danger" : ""
                          }`}
                          id="clientName"
                          placeholder="Enter client name"
                          label=""
                          error={errors.clientName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.clientName?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="qbClientName" required className="mb-1">
                        QB Client Name
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("qbClientName")}
                          className={`w-full  border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.qbClientName ? "border-badge-danger" : ""
                          }`}
                          id="qbClientName"
                          placeholder="Enter QB client name"
                          label=""
                          error={errors.qbClientName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.qbClientName?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="contactPerson" required className="mb-1">
                        Contact Person
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("contactPerson")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.contactPerson ? "border-badge-danger" : ""
                          }`}
                          id="contactPerson"
                          placeholder="Enter contact person name"
                          label=""
                          error={errors.contactPerson?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.contactPerson?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="Status"
                            name="status"
                            value={field.value}
                            onChange={field.onChange}
                            options={["Active", "On Hold", "Inactive"]}
                            placeholder="Select Status"
                            error={errors.status?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    {/* Add Contact Email */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="contactEmail"
                        className="font-medium text-text-primary mb-1"
                      >
                        Contact Email
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="email"
                          {...register("contactEmail")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.contactEmail ? "border-badge-danger" : ""
                          }`}
                          id="contactEmail"
                          placeholder="Enter contact email"
                          label=""
                          error={errors.contactEmail?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.contactEmail?.message} className="mt-1" />
                    </div>

                    {/* Max Hours Monthly */}
                    <div className="w-full min-w-0 xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="maxHoursMonthly"
                        className="font-medium text-text-primary mb-1"
                      >
                        Max Hours Monthly
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="number"
                          {...register("maxHoursMonthly")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.maxHoursMonthly ? "border-badge-danger" : ""
                          }`}
                          id="maxHoursMonthly"
                          placeholder="Enter max hours per month"
                          min="0"
                          label=""
                          error={errors.maxHoursMonthly?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.maxHoursMonthly?.message} className="mt-1" />
                      <Typography
                        variant="caption"
                        className="text-text-secondary block mt-0.5 min-w-0 max-w-full break-words leading-snug"
                      >
                        Changing monthly limit does not affect locked months.
                      </Typography>
                    </div>
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-wrap items-center gap-2">
                            <Typography
                              variant="text2"
                              component="label"
                              className="font-medium text-text-primary w-full mb-1 block"
                            >
                              Put client on hold
                            </Typography>
                            <ToggleSwitch
                              checked={field.value === "On Hold"}
                              onChange={(checked) => field.onChange(checked ? "On Hold" : "Active")}
                              size="md"
                              aria-label="Put client on hold"
                            />
                          </div>
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="isSaasClient"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-wrap items-center gap-2">
                            <Label className="w-full mb-1 block">SaaS client</Label>
                            <ToggleSwitch
                              checked={!!field.value}
                              onChange={field.onChange}
                              size="md"
                              aria-label="SaaS client"
                            />
                          </div>
                        )}
                      />
                    </div>

                    {/* SaaS modules — quick add only modules this client uses */}
                    <Controller
                      name="isSaasClient"
                      control={control}
                      render={({ field: { value: isSaasClientVal } }) =>
                        isSaasClientVal && saasModuleList.length > 0 ? (
                          <ClientSaasModulesSection
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            unregister={unregister}
                            saasModuleCatalog={saasModuleList}
                            assignedModuleIds={assignedSaasModuleIds}
                            onAssignedModuleIdsChange={setAssignedSaasModuleIds}
                          />
                        ) : null
                      }
                    />

                    {/* Server Hosted */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="serverHosted"
                        className="font-medium text-text-primary mb-1"
                      >
                        Server Hosted
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("serverHosted")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="serverHosted"
                          placeholder="Enter server hosted"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Web IP Address */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="ipAddress"
                        className="font-medium text-text-primary mb-1"
                      >
                        Web IP Address
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("ipAddress")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="ipAddress"
                          placeholder="Enter web IP address"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DB IP Address */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="dbIpAddress"
                        className="font-medium text-text-primary mb-1"
                      >
                        DB IP Address
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("dbIpAddress")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="dbIpAddress"
                          placeholder="Enter DB IP address"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DB Language */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        DB Language
                      </Typography>
                      <Controller
                        name="dbLanguageId"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            name="dbLanguageId"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target?.value ?? "")}
                            options={dbLanguageOptions}
                            placeholder="Search and select DB language"
                            searchable
                            noWrapper
                            className="text-sm w-full"
                          />
                        )}
                      />
                    </div>

                    {/* Mail Hosted */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="mailHosted"
                        className="font-medium text-text-primary mb-1"
                      >
                        Mail Hosted
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("mailHosted")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="mailHosted"
                          placeholder="Enter mail hosted"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Domain Name */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="domainName"
                        className="font-medium text-text-primary mb-1"
                      >
                        Domain Name
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("domainName")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="domainName"
                          placeholder="Enter domain name"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DNS */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="dns"
                        className="font-medium text-text-primary mb-1"
                      >
                        DNS
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("dns")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="dns"
                          placeholder="Enter DNS"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Domain Registrar */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="domainRegistrar"
                        className="font-medium text-text-primary mb-1"
                      >
                        Domain Registrar
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("domainRegistrar")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.domainRegistrar ? "border-badge-danger" : ""
                          }`}
                          id="domainRegistrar"
                          placeholder="Enter domain registrar"
                          maxLength={255}
                          label=""
                          error={errors.domainRegistrar?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.domainRegistrar?.message} className="mt-1" />
                    </div>

                    {/* ITP Access */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="itpAccess"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="ITP Access"
                            name="itpAccess"
                            value={field.value}
                            onChange={field.onChange}
                            options={ITP_ACCESS_DROPDOWN_OPTIONS}
                            placeholder="Select ITP Access"
                            searchable={false}
                            clearable={false}
                            preserveOptionOrder={true}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    {/* Upload Attachments */}
                    <div className="w-full mb-2">
                      <FileDropzone
                        label="Attachments"
                        onFilesChange={(files) => setAttachments(files)}
                        maxFiles={10}
                        maxSizeMB={10}
                        inspectContent={false}
                      />
                    </div>

                    <div className="w-full mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="notes"
                        className=" font-medium text-text-primary mb-1"
                      >
                        Notes
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <textarea
                          {...register("notes")}
                          className={`w-full  border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.notes ? "border-badge-danger" : ""
                          }`}
                          id="notes"
                          placeholder="Enter notes"
                          rows="3"
                        />
                      </div>
                      <ErrorMsg message={errors.notes?.message} className="mt-1" />
                    </div>
                  </div>

                  {/* Submit Button - Bottom Right */}
                  <div className="flex flex-wrap -mx-4 mt-3">
                    <div className="w-full px-4 text-end">
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="danger"
                          size="md"
                          disabled={loading}
                          onClick={() => navigate(prevPath)}
                          className="btn-reset"
                        >
                          Cancel
                        </Button>
                        <FormSubmitButton
                          loading={loading}
                          label="Add Client"
                          idleIconClass="ri-add-circle-line"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
